import { SearchIcon } from '@chakra-ui/icons';
import {
  Badge,
  Box,
  Button,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import React, { useMemo, useState } from 'react';

import { MenuRelationDetail } from '@/pages/externals/pos-menu-masters/components/MenuRelationDetail';
import {
  categoryLabel,
  diffShortLabel,
  displayPrice,
  formatYen,
  hasChildren,
  matchesKeyword,
  menuCategory,
  PreviewFilter,
} from '@/pages/externals/pos-menu-masters/service';
import { DiffType, MosPosDiff, MosPosMenu } from '@/services/MosPosMenuMaster';

type Props = {
  menus: MosPosMenu[];
  diff: MosPosDiff;
  diffByMenu: Record<string, DiffType>;
  businessDate: string;
  children?: React.ReactNode;
};

const chips = (diff: MosPosDiff) => [
  { key: 'all' as const, label: 'すべて', value: diff.new + diff.update },
  { key: 'new' as const, label: '新規登録', value: diff.new },
  {
    key: 'update' as const,
    label: '更新（上書き）',
    value: diff.update,
  },
  {
    key: 'unchanged' as const,
    label: '変更なし（更新の内数）',
    value: diff.unchanged,
  },
];

const filterPills: Array<{ key: PreviewFilter; label: string }> = [
  { key: 'all', label: 'すべて' },
  { key: 'new', label: '新規' },
  { key: 'update', label: '更新' },
  { key: 'unchanged', label: '変更なし' },
  { key: 'single', label: '単品' },
  { key: 'set', label: 'セット' },
];

const isUpdateLike = (type: DiffType | undefined) =>
  type === 'update' || type === 'unchanged';

const initialExpandedCodes = (
  menus: MosPosMenu[],
  diffByMenu: Record<string, DiffType>
) =>
  new Set(
    menus
      .filter(
        (menu) =>
          hasChildren(menu) && isUpdateLike(diffByMenu[menu.mets_menu_cd])
      )
      .map((menu) => menu.mets_menu_cd)
  );

const diffColor = (type: DiffType): string => {
  switch (type) {
    case 'new':
      return 'green';
    case 'update':
      return 'blue';
    default:
      return 'gray';
  }
};

const chipPalette = (key: PreviewFilter) => {
  switch (key) {
    case 'new':
      return { background: '#F0FFF4', border: '#9AE6B4', value: '#276749' };
    case 'update':
      return { background: '#E3F0FF', border: '#7FB3F0', value: '#1D4ED8' };
    case 'unchanged':
      return { background: '#EDF2F7', border: '#CBD5E0', value: '#5A6473' };
    default:
      return { background: '#F5F8FB', border: '#E2E8F0', value: '#2D3748' };
  }
};

const tableHeaderStyle: React.CSSProperties = {
  background: '#F5F8FB',
  borderBottom: '1px solid #E2E8F0',
  color: '#718096',
  fontSize: '11px',
  fontWeight: 700,
  letterSpacing: '0.04em',
  padding: '12px 14px',
  position: 'sticky',
  textTransform: 'uppercase',
  top: 0,
  zIndex: 1,
};

const tableBadgeStyle: React.CSSProperties = {
  alignItems: 'center',
  borderRadius: '4px',
  display: 'inline-flex',
  fontSize: '11px',
  justifyContent: 'center',
  lineHeight: 1.6,
  minWidth: '42px',
  padding: '2px 8px',
};

/**
 * 取込差分のプレビュー。絞り込み・検索・展開はすべて手元のデータで行い、
 * 追加のリクエストは出しません。
 */
export const ImportPreview: React.FC<Props> = ({
  menus,
  diff,
  diffByMenu,
  businessDate,
  children,
}) => {
  const [filter, setFilter] = useState<PreviewFilter>('all');
  const [keyword, setKeyword] = useState('');
  const [expanded, setExpanded] = useState<Set<string>>(() =>
    initialExpandedCodes(menus, diffByMenu)
  );

  const visible = useMemo(
    () =>
      menus.filter((menu) => {
        if (!matchesKeyword(menu, keyword)) return false;
        if (filter === 'all') return true;
        if (filter === 'single' || filter === 'set') {
          return menuCategory(menu) === filter;
        }
        if (filter === 'update') {
          return isUpdateLike(diffByMenu[menu.mets_menu_cd]);
        }
        return diffByMenu[menu.mets_menu_cd] === filter;
      }),
    [menus, keyword, filter, diffByMenu]
  );

  const toggle = (code: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });

  const selectFilter = (next: PreviewFilter) => {
    setFilter(next);
    // 更新には「変更なし」の内数も含まれるため、どちらも確認しやすいよう展開します。
    if (next === 'update') {
      setExpanded(initialExpandedCodes(menus, diffByMenu));
    }
  };

  // 展開できるのは子商品を持つ行だけなので、全展開の判定もその行数で見ます。
  const expandableCodes = visible
    .filter((m) => hasChildren(m))
    .map((m) => m.mets_menu_cd);
  const allExpanded =
    expandableCodes.length > 0 &&
    expandableCodes.every((code) => expanded.has(code));

  const expandAll = () =>
    setExpanded(allExpanded ? new Set() : new Set(expandableCodes));

  return (
    <Box
      data-testid="preview-card"
      style={{
        background: 'white',
        border: '1px solid #E2E8F0',
        borderRadius: '5px',
        boxShadow: '0 1px 2px rgba(45,55,72,0.04)',
        overflow: 'hidden',
      }}
    >
      <Flex
        align="center"
        data-testid="preview-section-head"
        style={{
          borderBottom: '1px solid #E2E8F0',
          gap: '10px',
          padding: '15px 20px',
        }}
      >
        <Text style={{ fontSize: '15px', fontWeight: 700 }}>
          取込内容の確認
        </Text>
        <Text color="gray.500" style={{ fontSize: '12px', marginLeft: 'auto' }}>
          登録前に差分を確認できます
        </Text>
      </Flex>

      <Box style={{ padding: '20px' }}>
        <Text
          data-testid="preview-diff-label"
          style={{ fontSize: '12px', fontWeight: 700, marginBottom: '8px' }}
        >
          取込差分サマリ
        </Text>
        <Flex style={{ gap: '12px', marginBottom: '8px', flexWrap: 'wrap' }}>
          {chips(diff).map((chip) => {
            const palette = chipPalette(chip.key);
            return (
              <Button
                key={chip.key}
                variant="unstyled"
                data-testid={`diff-chip-${chip.key}`}
                aria-pressed={filter === chip.key}
                onClick={() => selectFilter(chip.key)}
                style={{
                  background: palette.background,
                  border: `1px solid ${palette.border}`,
                  borderRadius: '5px',
                  boxShadow:
                    filter === chip.key ? 'inset 0 0 0 2px #394867' : 'none',
                  flex: '1',
                  height: 'auto',
                  minWidth: '140px',
                  padding: '12px 16px',
                }}
              >
                <Box textAlign="center">
                  <Text
                    style={{
                      color: palette.value,
                      fontSize: '18px',
                      fontWeight: 700,
                      lineHeight: 1.1,
                    }}
                  >
                    {chip.value}
                  </Text>
                  <Text fontSize="xs" fontWeight="normal" color="gray.600">
                    {chip.label}
                  </Text>
                </Box>
              </Button>
            );
          })}
        </Flex>

        <Text
          fontSize="xs"
          color="gray.600"
          style={{ marginBottom: '12px' }}
          data-testid="preview-unchanged-note"
        >
          「変更なし」は更新の内数です（新規＋更新＝すべて）。確認用の表示のみで、内容が同じ商品も今回のCSVの内容で上書き登録されます。
        </Text>
        <Text
          fontSize="xs"
          color="gray.600"
          style={{ marginBottom: '12px' }}
          data-testid="preview-scope-note"
        >
          今回のCSVに含まれない商品は変更されません。取り込むCSVに含まれている商品のみが登録・更新の対象になります。
        </Text>

        <Flex
          align="center"
          style={{ gap: '12px', marginBottom: '10px', flexWrap: 'wrap' }}
        >
          <InputGroup size="sm" maxW="320px">
            <InputLeftElement pointerEvents="none">
              <SearchIcon color="gray.500" data-testid="preview-search-icon" />
            </InputLeftElement>
            <Input
              placeholder="POSメニューコード・商品名で検索"
              value={keyword}
              data-testid="preview-search"
              onChange={(e) => setKeyword(e.target.value)}
              style={{ borderRadius: '4px' }}
            />
          </InputGroup>
          <Flex
            aria-label="取込内容フィルター"
            data-testid="preview-filter-pills"
            style={{ alignItems: 'center', flexWrap: 'wrap', gap: '6px' }}
          >
            {filterPills.map((pill) => {
              const selected = filter === pill.key;
              return (
                <React.Fragment key={pill.key}>
                  {/* 取込区分の絞り込みと区分の絞り込みは別の軸なので区切ります。 */}
                  {pill.key === 'single' && (
                    <Box
                      aria-hidden="true"
                      data-testid="preview-filter-separator"
                      style={{
                        background: '#CBD5E0',
                        height: '18px',
                        margin: '0 2px',
                        width: '1px',
                      }}
                    />
                  )}
                  <Button
                    size="sm"
                    variant="unstyled"
                    aria-pressed={selected}
                    data-testid={`preview-filter-${pill.key}`}
                    onClick={() => selectFilter(pill.key)}
                    style={{
                      background: selected ? '#394867' : '#FFFFFF',
                      border: `1px solid ${selected ? '#394867' : '#CBD5E0'}`,
                      borderRadius: '999px',
                      color: selected ? '#FFFFFF' : '#4A5568',
                      fontSize: '12px',
                      fontWeight: selected ? 700 : 400,
                      height: '28px',
                      minWidth: '0',
                      padding: '0 10px',
                    }}
                  >
                    {pill.label}
                  </Button>
                </React.Fragment>
              );
            })}
          </Flex>
          <Box data-testid="preview-toolbar-spacer" style={{ flex: '1' }} />
          <Button
            size="sm"
            variant="outline"
            data-testid="expand-all"
            onClick={expandAll}
            style={{ borderRadius: '4px', fontSize: '12px', fontWeight: 700 }}
          >
            {allExpanded ? 'すべて折りたたむ' : 'すべて展開'}
          </Button>
          <Text fontSize="xs" color="gray.500" data-testid="preview-count">
            表示 {visible.length} / 全 {menus.length} 件
          </Text>
        </Flex>

        <Box
          data-testid="preview-table-wrap"
          style={{
            border: '1px solid #E2E8F0',
            borderRadius: '5px',
            overflowX: 'auto',
            overflowY: 'hidden',
          }}
        >
          <Table size="sm" style={{ minWidth: '900px' }}>
            <Thead
              data-testid="preview-table-head"
              style={{
                background: '#F5F8FB',
                borderBottom: '1px solid #E2E8F0',
              }}
            >
              <Tr>
                <Th style={{ ...tableHeaderStyle, width: '38px' }} />
                <Th style={{ ...tableHeaderStyle, width: '140px' }}>
                  POSメニューコード
                </Th>
                <Th style={tableHeaderStyle}>商品名</Th>
                <Th style={{ ...tableHeaderStyle, width: '74px' }}>区分</Th>
                <Th isNumeric style={{ ...tableHeaderStyle, width: '86px' }}>
                  販売価格
                </Th>
                <Th isNumeric style={{ ...tableHeaderStyle, width: '120px' }}>
                  軽減税率価格
                </Th>
                <Th isNumeric style={{ ...tableHeaderStyle, width: '70px' }}>
                  子商品
                </Th>
                <Th style={{ ...tableHeaderStyle, width: '96px' }}>取込区分</Th>
              </Tr>
            </Thead>
            <Tbody>
              {visible.map((menu) => {
                const type = diffByMenu[menu.mets_menu_cd] ?? 'unchanged';
                const price = displayPrice(menu, businessDate);
                const expandable = hasChildren(menu);
                const isOpen = expandable && expanded.has(menu.mets_menu_cd);
                return (
                  <React.Fragment key={menu.mets_menu_cd}>
                    <Tr
                      data-testid={`preview-row-${menu.mets_menu_cd}`}
                      onClick={() =>
                        expandable ? toggle(menu.mets_menu_cd) : undefined
                      }
                      cursor={expandable ? 'pointer' : 'default'}
                    >
                      <Td color={expandable ? 'gray.600' : 'gray.300'}>
                        {expandable ? (
                          <Button
                            variant="unstyled"
                            minW="0"
                            h="auto"
                            aria-expanded={isOpen}
                            aria-controls={`preview-detail-${menu.mets_menu_cd}`}
                            aria-label={`${menu.mets_screen_menu_name}の詳細を${
                              isOpen ? '折りたたむ' : '展開'
                            }`}
                            onClick={(event) => {
                              event.stopPropagation();
                              toggle(menu.mets_menu_cd);
                            }}
                          >
                            {isOpen ? '▾' : '▸'}
                          </Button>
                        ) : (
                          '▸'
                        )}
                      </Td>
                      <Td style={{ fontFamily: 'monospace' }}>
                        {menu.mets_menu_cd}
                      </Td>
                      <Td style={{ fontWeight: 700 }}>
                        {menu.mets_screen_menu_name}
                      </Td>
                      <Td data-testid={`preview-category-${menu.mets_menu_cd}`}>
                        <Badge
                          style={
                            menuCategory(menu) === 'set'
                              ? {
                                  ...tableBadgeStyle,
                                  background: '#FEEBC8',
                                  color: '#7B341E',
                                }
                              : {
                                  ...tableBadgeStyle,
                                  background: '#E6FFFA',
                                  color: '#234E52',
                                }
                          }
                        >
                          {categoryLabel(menuCategory(menu))}
                        </Badge>
                      </Td>
                      <Td isNumeric>{formatYen(price?.sell_price)}</Td>
                      <Td
                        isNumeric
                        data-testid={`preview-reduced-price-${menu.mets_menu_cd}`}
                      >
                        {formatYen(price?.reduced_sell_price)}
                      </Td>
                      <Td isNumeric color={expandable ? undefined : 'gray.400'}>
                        {menu.source_relation_count}
                      </Td>
                      <Td>
                        <Badge
                          colorScheme={diffColor(type)}
                          style={tableBadgeStyle}
                        >
                          {diffShortLabel(type)}
                        </Badge>
                      </Td>
                    </Tr>
                    {isOpen && (
                      <Tr
                        id={`preview-detail-${menu.mets_menu_cd}`}
                        data-testid={`preview-detail-${menu.mets_menu_cd}`}
                      >
                        <Td colSpan={8}>
                          <MenuRelationDetail
                            menu={menu}
                            businessDate={businessDate}
                          />
                        </Td>
                      </Tr>
                    )}
                  </React.Fragment>
                );
              })}
            </Tbody>
          </Table>
        </Box>

        <Text
          fontSize="xs"
          color="gray.600"
          mt={2}
          data-testid="preview-expand-note"
        >
          行を展開すると、セット構成（必ず含まれる構成品）・チョイス（お客様が選ぶ商品と価格差額）・付属品の詳細を確認できます。確認漏れを防ぐため、「更新」対象の行は初期表示で自動展開されます。
        </Text>
        {children}
      </Box>
    </Box>
  );
};
